import React from "react";
import { Heartbeat, Leaf, Scales } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const Sustainability = () => {
  const { t } = useLang();

  const pillars = [
    { icon: Heartbeat, title: t.sustainability.p1Title, body: t.sustainability.p1Body, no: "01" },
    { icon: Leaf, title: t.sustainability.p2Title, body: t.sustainability.p2Body, no: "02" },
    { icon: Scales, title: t.sustainability.p3Title, body: t.sustainability.p3Body, no: "03" },
  ];

  return (
    <>
      <SEO
        title="Sustainability & HSE"
        description="Health, safety, environment and governance at Masika Chem SARL — ARSP-compliant procurement and responsible chemistry for the African mining sector."
        path="/sustainability"
      />

      {/* HERO */}
      <section className="relative bg-neutral-900 text-white" data-testid="sus-hero">
        <img
          src="https://images.pexels.com/photos/35497312/pexels-photo-35497312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600"
          alt="Mineral close-up"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/80" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#E85A1F]" />
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#E85A1F]">
                {t.sustainability.overline}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              {t.sustainability.title}
            </h1>
            <p className="mt-7 text-lg text-neutral-300 max-w-2xl">{t.sustainability.sub}</p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white py-24" data-testid="sus-pillars">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="bg-white p-10 lg:p-12" data-testid={`sus-pillar-${i}`}>
                  <div className="flex items-center justify-between mb-8">
                    <Icon size={36} weight="duotone" className="text-[#16A34A]" />
                    <span className="text-xs font-bold tracking-[0.3em] text-neutral-400">
                      {p.no}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-900 mb-4 tracking-tight">{p.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMITMENT STATEMENT */}
      <section className="bg-[#F7F7F5] py-24" data-testid="sus-commitment">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center">
          <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">Commitment</span>
          <h2 className="mt-4 text-3xl lg:text-5xl font-extrabold text-neutral-900 leading-[1.1] tracking-tight">
            "We believe a reliable supply chain and responsible chemistry are inseparable —
            because Africa's industrial growth depends on both."
          </h2>
          <p className="mt-8 text-sm font-bold tracking-[0.2em] uppercase text-neutral-500">
            — Masika Chem SARL leadership
          </p>
        </div>
      </section>
    </>
  );
};

export default Sustainability;
